import { ViewmodelAgreementPreview, ViewmodelAgreement } from '@acordocertobr/reneg-offers-hub';
import { useQuery } from '@tanstack/react-query';
import { QUERY_AGREEMENTS_KEY, QUERY_AGREEMENT_KEY } from '../../constants/queryKeys';
import * as AgreementService from '../../services/agreements/agreements.service';
import { renegkitQueryClient } from '../../services/query/query.service';
import { useRenegTracking } from '../../hooks/useRenegTracking';

/**
 * Custom hook to manage agreements with additional features
 *
 * This hook encapsulates agreements fetching logic and adds useful features
 * like filters and specific state handling.
 *
 * Automatic cache: First call (either in carousel or agreements page)
 * will make the request. Subsequent calls will use cache automatically.
 *
 */
export const useAgreementsListQuery = (enabled = true) => {
  const { trackAgreementsLoaded, trackAgreementsLoadFailed } = useRenegTracking();

  const {
    data: agreements = [],
    isLoading,
    error,
    isError,
    refetch,
    isFetching,
    isSuccess,
  } = useQuery<ViewmodelAgreementPreview[], Error>(
    {
      queryKey: [QUERY_AGREEMENTS_KEY],
      queryFn: () =>
        AgreementService.getAgreementsList()
          .then((res) => {
            trackAgreementsLoaded(res);
            return res;
          })
          .catch((error) => {
            trackAgreementsLoadFailed(error);
            throw error;
          }),
      staleTime: 30 * 60 * 1000, // 30 min
      gcTime: 60 * 60 * 1000, // 1 hour
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      enabled,
    },
    renegkitQueryClient
  );

  // Sort agreements with ACTIVE first, then PAID, then CANCELLED
  const sortedAgreements = agreements.sort((a, b) => {
    const statusPriority = { ACTIVE: 0, PAID: 1, CANCELLED: 2 };
    return statusPriority[a.status] - statusPriority[b.status];
  });

  const activeAgreements = agreements.filter((agreement) => agreement.status === 'ACTIVE');

  const hasAgreements = agreements.length > 0;
  const hasActiveAgreements = activeAgreements.length > 0;

  const invalidateAgreements = () => {
    renegkitQueryClient.invalidateQueries({
      queryKey: [QUERY_AGREEMENTS_KEY],
    });
  };

  const refetchAgreements = () => {
    return renegkitQueryClient.refetchQueries({
      queryKey: [QUERY_AGREEMENTS_KEY],
    });
  };

  const removeAgreementsCache = () => {
    renegkitQueryClient.removeQueries({
      queryKey: [QUERY_AGREEMENTS_KEY],
    });
  };

  return {
    // Main data (sorted with ACTIVE first)
    agreements: sortedAgreements,

    // Query states
    isLoading,
    isFetching,
    isError,
    isSuccess,
    error,

    // Additional features
    activeAgreements,
    hasAgreements,
    hasActiveAgreements,
    refetch,

    // Total agreements count
    totalAgreements: agreements.length,

    // cache manager
    invalidateAgreements,
    refetchAgreements,
    removeAgreementsCache,
  };
};

/**
 * Custom hook to fetch a specific agreement by code
 *
 * This hook fetches a single agreement's detailed information using the agreement code.
 * It includes tracking events and provides useful state information.
 *
 * @param agreementCode - The agreement code to fetch
 * @param enabled - Whether to enable the query (default: true when agreementCode is provided)
 */
export const useAgreementQuery = (agreementCode: string, enabled?: boolean) => {
  const { trackAgreementDetailLoaded, trackAgreementDetailLoadFailed } = useRenegTracking();

  const {
    data: agreement,
    isLoading,
    error,
    isError,
    refetch,
    isFetching,
    isSuccess,
  } = useQuery<ViewmodelAgreement, Error>(
    {
      queryKey: [QUERY_AGREEMENT_KEY, agreementCode],
      queryFn: () =>
        AgreementService.getAgreement(agreementCode)
          .then((res) => {
            trackAgreementDetailLoaded(res);
            return res;
          })
          .catch((error) => {
            trackAgreementDetailLoadFailed(error, agreementCode);
            throw error;
          }),
      staleTime: 3 * 60 * 60 * 1000, // 3 hours (agreement details are very stable)
      gcTime: 4 * 60 * 60 * 1000, // 4 hours
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: enabled && !!agreementCode,
    },
    renegkitQueryClient
  );

  /**
   * Check if agreement exists in cache without triggering a fetch
   * @returns boolean indicating if valid cache data exists for this agreement
   */
  const checkCacheOnly = () => {
    const queryState = renegkitQueryClient.getQueryState([QUERY_AGREEMENT_KEY, agreementCode]);
    return queryState?.status === 'success' && !!queryState?.data;
  };

  const invalidateAgreement = () => {
    renegkitQueryClient.invalidateQueries({
      queryKey: [QUERY_AGREEMENT_KEY, agreementCode],
    });
  };

  const refetchAgreement = () => {
    return renegkitQueryClient.refetchQueries({
      queryKey: [QUERY_AGREEMENT_KEY, agreementCode],
    });
  };

  const removeAgreementCache = () => {
    renegkitQueryClient.removeQueries({
      queryKey: [QUERY_AGREEMENT_KEY, agreementCode],
    });
  };

  return {
    // Main data
    agreement,

    // Query states
    isLoading,
    isFetching,
    isError,
    isSuccess,
    error,

    // Additional helpers
    hasAgreement: !!agreement,
    agreementCode,
    refetch,

    // Cache utilities
    checkCacheOnly,
    invalidateAgreement,
    refetchAgreement,
    removeAgreementCache,
  };
};
